import { tokenizeQcValues } from 'commons-app/utils/qc-values.utils';
import { isNotEmpty } from 'commons/utils/string.utils';
import { LONG_NAME_MAX_LENGTH, NAME_MAX_LENGTH, PROVIDER_MAX_LENGTH } from '../material-definitions.constants';
import {
    LOT_NUMBER_MAX_LENGTH_MAX,
    LOT_NUMBER_MAX_LENGTH_MIN,
    MAX_QC_LEVELS_PER_TEST,
    MaterialDefinitionFormState,
    QcLevelFormItem,
    TestConfigFormItem,
} from './settings-material-definitions-form.model';

const QC_LEVELS_ERROR_KEY = 'settings.materials.material-definitions.form.qc-config.levels.errors';
const QC_ERRORS_KEY = 'settings.materials.material-definitions.form.qc-config.errors';

const exceedsMaxLength = (value: string, max: number): boolean => value.length > max;

const hasDuplicateTestIds = (testConfigs: TestConfigFormItem[]): boolean => {
    const ids = testConfigs.map((testConfig) => testConfig.testId).filter((id) => id !== '');
    return new Set(ids).size !== ids.length;
};

export const validateTestSelections = (testConfigs: TestConfigFormItem[]): string | undefined => {
    if (testConfigs.some((testConfig) => testConfig.testId.trim() === '')) {
        return `${QC_ERRORS_KEY}.test-required`;
    }
    if (hasDuplicateTestIds(testConfigs)) {
        return `${QC_ERRORS_KEY}.duplicate-test`;
    }
    return undefined;
};

const minGreaterThanMax = (level: QcLevelFormItem): boolean =>
    level.defaultMin != null && level.defaultMax != null && level.defaultMin > level.defaultMax;

const hasNegativeDeviation = (level: QcLevelFormItem): boolean =>
    level.defaultDeviationPercent != null && level.defaultDeviationPercent < 0;

const hasDuplicateNames = (levels: QcLevelFormItem[]): boolean => {
    const names = levels.map((level) => level.name.trim());
    return new Set(names).size !== names.length;
};

const firstStructureError = (levels: QcLevelFormItem[]): string | undefined => {
    if (levels.length === 0) {
        return `${QC_LEVELS_ERROR_KEY}.at-least-one`;
    }
    if (levels.length > MAX_QC_LEVELS_PER_TEST) {
        return `${QC_LEVELS_ERROR_KEY}.max-levels`;
    }
    if (levels.some((level) => level.name.trim() === '')) {
        return `${QC_LEVELS_ERROR_KEY}.required-name`;
    }
    if (hasDuplicateNames(levels)) {
        return `${QC_LEVELS_ERROR_KEY}.duplicate-name`;
    }
    return undefined;
};

const firstNumericError = (levels: QcLevelFormItem[]): string | undefined => {
    if (levels.some(minGreaterThanMax)) {
        return `${QC_LEVELS_ERROR_KEY}.min-gt-max`;
    }
    if (levels.some(hasNegativeDeviation)) {
        return `${QC_LEVELS_ERROR_KEY}.negative-deviation`;
    }
    return undefined;
};

const firstLevelError = (testConfig: TestConfigFormItem): string | undefined => {
    const levels = testConfig.qcLevels ?? [];
    const structureError = firstStructureError(levels);
    if (structureError) {
        return structureError;
    }
    return testConfig.resultType === 'QUANTITATIVE' ? firstNumericError(levels) : undefined;
};

export const validateQcLevels = (testConfigs: TestConfigFormItem[]): string | undefined =>
    testConfigs.map(firstLevelError).find((error) => error !== undefined);

const QC_TARGET_NOT_IN_POSSIBLE_KEY = `${QC_LEVELS_ERROR_KEY}.target-not-in-possible-values`;
const QC_TARGET_NO_POSSIBLE_KEY = `${QC_LEVELS_ERROR_KEY}.target-no-possible-values`;

const targetValuesError = (
    possibleValues: string | undefined,
    targetValues: string | undefined
): string | undefined => {
    const targets = tokenizeQcValues(targetValues);
    if (targets.length === 0) {
        return undefined;
    }
    const possible = tokenizeQcValues(possibleValues);
    if (possible.length === 0) {
        return QC_TARGET_NO_POSSIBLE_KEY;
    }
    return targets.some((target) => !possible.includes(target)) ? QC_TARGET_NOT_IN_POSSIBLE_KEY : undefined;
};

const targetErrorsForTest = (testConfig: TestConfigFormItem): Record<number, string> => {
    if (testConfig.resultType !== 'QUALITATIVE') {
        return {};
    }
    const errors: Record<number, string> = {};
    (testConfig.qcLevels ?? []).forEach((level, index) => {
        const error = targetValuesError(testConfig.possibleValues, level.defaultTargetValues);
        if (error) {
            errors[index] = error;
        }
    });
    return errors;
};

// Per-row Target-values errors, keyed by test clientKey then level index, so QcLevelLine
// can show inline validity on the exact offending input (a section error alone is not enough).
export type QcTargetErrors = Record<string, Record<number, string>>;

export const validateQcTargetValues = (testConfigs: TestConfigFormItem[]): QcTargetErrors => {
    const result: QcTargetErrors = {};
    testConfigs.forEach((testConfig) => {
        const levelErrors = targetErrorsForTest(testConfig);
        if (Object.keys(levelErrors).length > 0) {
            result[testConfig.clientKey] = levelErrors;
        }
    });
    return result;
};

// Single source of truth for the test-config section error, reused by validateRequiredFields
// (save gating) and the live section error shown on the QC card. Target-values errors are shown
// inline per row (validateQcTargetValues) and gate Save via isFormDataValid — never surfaced in the
// section banner, which stays result-type-symmetric with the quantitative levels.
export const getTestConfigsError = (testConfigs: TestConfigFormItem[]): string | undefined => {
    if (testConfigs.length === 0) {
        return 'common.forms.errors.value-missing';
    }
    return validateTestSelections(testConfigs) ?? validateQcLevels(testConfigs);
};

const areQcLevelsEqual = (current?: QcLevelFormItem[], initial?: QcLevelFormItem[]): boolean => {
    const left = current ?? [];
    const right = initial ?? [];
    if (left.length !== right.length) {
        return false;
    }
    return left.every(
        (level, index) =>
            level.name === right[index].name &&
            (level.ordinal ?? null) === (right[index].ordinal ?? null) &&
            (level.defaultMin ?? null) === (right[index].defaultMin ?? null) &&
            (level.defaultMax ?? null) === (right[index].defaultMax ?? null) &&
            (level.defaultTarget ?? null) === (right[index].defaultTarget ?? null) &&
            (level.defaultDeviationPercent ?? null) === (right[index].defaultDeviationPercent ?? null) &&
            (level.defaultTargetValues ?? '') === (right[index].defaultTargetValues ?? '')
    );
};

export const validateLotNumberMaxLength = (value: number | null): string | undefined => {
    if (
        value === null ||
        !Number.isInteger(value) ||
        value < LOT_NUMBER_MAX_LENGTH_MIN ||
        value > LOT_NUMBER_MAX_LENGTH_MAX
    ) {
        return 'settings.materials.material-definitions.form.errors.lot-number-max-length-range';
    }
    return undefined;
};

export const isFormDataValid = (data: MaterialDefinitionFormState): boolean =>
    Object.keys(validateRequiredFields(data)).length === 0 &&
    Object.keys(validateQcTargetValues(data.testConfigs)).length === 0;

export const hasDataChanged = (current: MaterialDefinitionFormState, initial: MaterialDefinitionFormState): boolean =>
    current.name !== initial.name ||
    current.longName !== initial.longName ||
    current.provider !== initial.provider ||
    current.type !== initial.type ||
    current.deviceTypeId !== initial.deviceTypeId ||
    current.testConfigs.length !== initial.testConfigs.length ||
    current.testConfigs.some(
        (testConfig, index) =>
            testConfig.testId !== initial.testConfigs[index]?.testId ||
            testConfig.resultType !== initial.testConfigs[index]?.resultType ||
            (testConfig.rangeMode ?? null) !== (initial.testConfigs[index]?.rangeMode ?? null) ||
            (testConfig.possibleValues ?? '') !== (initial.testConfigs[index]?.possibleValues ?? '') ||
            !areQcLevelsEqual(testConfig.qcLevels, initial.testConfigs[index]?.qcLevels)
    ) ||
    current.lotNumberMaxLength !== initial.lotNumberMaxLength ||
    current.allowAlphanumericLotNumbers !== initial.allowAlphanumericLotNumbers;

export type FormErrors = Partial<Record<keyof MaterialDefinitionFormState, string>>;

export const validateRequiredFields = (data: MaterialDefinitionFormState): FormErrors => {
    const errors: FormErrors = {};
    if (!isNotEmpty(data.name)) {
        errors.name = 'common.forms.errors.value-missing';
    } else if (exceedsMaxLength(data.name, NAME_MAX_LENGTH)) {
        errors.name = 'common.forms.errors.too-long';
    }
    if (exceedsMaxLength(data.longName, LONG_NAME_MAX_LENGTH)) {
        errors.longName = 'common.forms.errors.too-long';
    }
    if (!isNotEmpty(data.provider)) {
        errors.provider = 'common.forms.errors.value-missing';
    } else if (exceedsMaxLength(data.provider, PROVIDER_MAX_LENGTH)) {
        errors.provider = 'common.forms.errors.too-long';
    }
    if (data.type === '') {
        errors.type = 'common.forms.errors.value-missing';
    }
    if (!isNotEmpty(data.deviceTypeId)) {
        errors.deviceTypeId = 'common.forms.errors.value-missing';
    }
    const testConfigsError = getTestConfigsError(data.testConfigs);
    if (testConfigsError) {
        errors.testConfigs = testConfigsError;
    }
    const lotNumberMaxLengthError = validateLotNumberMaxLength(data.lotNumberMaxLength);
    if (lotNumberMaxLengthError) {
        errors.lotNumberMaxLength = lotNumberMaxLengthError;
    }
    return errors;
};
